import React, { useEffect, useMemo, useRef, useState } from "react";
import { _cs } from "@togglecorp/fujs";

import List from "../List";

import styles from "./styles.module.scss";

export interface ViewConfig<P = Record<string, unknown>> {
  component: React.ComponentType<P>;
  rendererParams?: () => P;
  wrapContainer?: boolean;
  mount?: boolean;
  lazyMount?: boolean;
}

export interface MultiViewContainerProps<P = Record<string, unknown>> {
  className?: string;
  views: Record<string, ViewConfig<P>>;
  active?: string | number;
  activeClassName?: string;
  containerClassName?: string;
  useHash?: boolean;
}

type LazyContainerProps<P> = {
  view: ViewConfig<P>;
  isActive: boolean;
  props: P;
};

const MultiViewContainer = <P extends Record<string, unknown>>({
  views,
  active = undefined,
  activeClassName = styles.active,
  containerClassName,
  useHash = false,
}: MultiViewContainerProps<P>) => {
  const [hash, setHash] = useState<string | undefined>(
    useHash ? window.location.hash.substring(2) : undefined
  );

  const lazyContainers = useRef<
    Record<string, React.FC<LazyContainerProps<P>>>
  >({});

  const getHash = () => window.location.hash.substring(2);

  useEffect(() => {
    if (!useHash) return;

    const onHashChange = () => setHash(getHash());

    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [useHash]);

  const renderContainer = ({
    view,
    isActive,
    props,
  }: LazyContainerProps<P>) => {
    const Component = view.component;

    if (view.wrapContainer) {
      return (
        <div
          className={_cs(
            containerClassName,
            styles.container,
            isActive && activeClassName
          )}
        >
          <Component {...props} />
        </div>
      );
    }

    return (
      <Component
        {...props}
        className={isActive ? activeClassName : undefined}
      />
    );
  };

  const renderView = (key: string) => {
    const view = views[key];
    if (!view) return null;

    const isActive = useHash ? hash === key : String(active) === key;

    if (!view.mount && !isActive) return null;

    const params = view.rendererParams?.() ?? ({} as P);

    if (view.lazyMount) {
      if (!lazyContainers.current[key]) {
        if (!isActive) return null;

        lazyContainers.current[key] = (props) => renderContainer(props);
      }

      const LazyComponent = lazyContainers.current[key];
      return (
        <LazyComponent
          key={key}
          view={view}
          isActive={isActive}
          props={params}
        />
      );
    }

    return renderContainer({ view, isActive, props: params });
  };

  const viewList = useMemo(() => Object.keys(views), [views]);

  return (
    <List
      data={viewList}
      keySelector={(item) => item}
      modifier={(key: string) => renderView(key)}
    />
  );
};

export default MultiViewContainer;
